import { useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Home,
  MessageSquare,
  BookOpen,
  Layers,
  FileText,
  Target,
  Headphones,
  Video,
  GraduationCap,
  BarChart3,
  Sun,
  Moon,
  LogOut,
  Shield,
  X,
} from 'lucide-react';
import { useThemeStore, useAuthStore, useFlashcardsStore, useUIStore } from '@/stores';
import { signOut } from '@/lib/supabase';
import { Avatar, Badge } from '@/components/ui';
import { SidebarAd } from '@/components/AdBanner';
import { UploadIndicator, UploadStatusToast } from '@/components/UploadIndicator';
import { GenerationIndicator, GenerationStatusToast } from '@/components/GenerationIndicator';
import { cn } from '@/lib/utils';

const mainNavItems = [
  { icon: Home, label: 'Dashboard', path: '/dashboard' },
  { icon: MessageSquare, label: 'Ask AI', path: '/ask' },
  { icon: BookOpen, label: 'Reader', path: '/reader' },
];

const studyNavItems = [
  { icon: Layers, label: 'Flashcards', path: '/flashcards', showDueBadge: true },
  { icon: FileText, label: 'Quizzes', path: '/quizzes' },
  { icon: Target, label: 'Practice Tests', path: '/practice', premium: true },
];

const premiumNavItems = [
  { icon: Headphones, label: 'Podcasts', path: '/podcasts', premium: true },
  { icon: Video, label: 'Lectures', path: '/lectures', premium: true },
];

const organizeNavItems = [
  { icon: GraduationCap, label: 'Classes', path: '/classes' },
  { icon: BarChart3, label: 'Progress', path: '/progress' },
];

export function Sidebar() {
  const location = useLocation();
  const { theme, toggleTheme } = useThemeStore();
  const { user } = useAuthStore();
  const { dueToday } = useFlashcardsStore();
  const { mobileMenuOpen, setMobileMenuOpen } = useUIStore();

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname, setMobileMenuOpen]);

  const handleSignOut = async () => {
    await signOut();
    window.location.href = '/login';
  };

  const NavItem = ({
    icon: Icon,
    label,
    path,
    showDueBadge,
    premium
  }: {
    icon: typeof Home;
    label: string;
    path: string;
    showDueBadge?: boolean;
    premium?: boolean;
  }) => {
    const isActive = location.pathname === path || location.pathname.startsWith(path + '/');
    const dueCount = showDueBadge ? dueToday.length : 0;

    return (
      <NavLink
        to={path}
        className={cn(
          'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all',
          isActive
            ? 'bg-orange-500/10 text-accent-primary'
            : 'text-foreground-secondary hover:bg-background-tertiary hover:text-foreground-primary'
        )}
      >
        <Icon size={20} />
        <span className="flex-1">{label}</span>
        {showDueBadge && dueCount > 0 && <Badge>{dueCount}</Badge>}
        {premium && <Badge variant="premium">PRO</Badge>}
      </NavLink>
    );
  };

  const NavSection = ({
    label,
    children
  }: {
    label: string;
    children: React.ReactNode;
  }) => (
    <div className="mb-6">
      <div className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-foreground-muted">
        {label}
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );

  const sidebarContent = (
    <aside className={cn(
      'w-[260px] bg-background-secondary border-r border-border-subtle flex flex-col h-screen max-h-screen overflow-hidden fixed left-0 top-0 z-50',
      // Hidden on mobile unless mobileMenuOpen
      'max-md:hidden',
      mobileMenuOpen && 'max-md:flex'
    )}>
      {/* Header */}
      <div className="flex-shrink-0 px-4 py-5 border-b border-border-subtle flex items-center justify-between">
        <NavLink to="/" className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-accent-primary to-accent-secondary flex items-center justify-center">
            <div className="w-2.5 h-2.5 bg-white rounded-full" />
          </div>
          <span className="font-serif text-xl">Koda</span>
        </NavLink>

        <div className="flex items-center gap-2">
          <button
            onClick={toggleTheme}
            title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
            className="w-8 h-8 rounded-lg bg-background-tertiary border border-border-subtle flex items-center justify-center text-foreground-secondary hover:text-foreground-primary transition-colors"
          >
            {theme === 'light' ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          {/* Close button - mobile only */}
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="w-8 h-8 rounded-lg bg-background-tertiary border border-border-subtle flex items-center justify-center text-foreground-secondary hover:text-foreground-primary transition-colors md:hidden"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Upload Indicator */}
      <div className="flex-shrink-0">
        <UploadIndicator />
        <UploadStatusToast />
      </div>

      {/* Generation Indicator */}
      <GenerationIndicator />
      <GenerationStatusToast />

      {/* Navigation - scrollable */}
      <nav className="flex-1 min-h-0 px-3 py-4 overflow-y-auto">
        <NavSection label="Main">
          {mainNavItems.map((item) => (
            <NavItem key={item.path} {...item} />
          ))}
        </NavSection>

        <NavSection label="Study">
          {studyNavItems.map((item) => (
            <NavItem key={item.path} {...item} />
          ))}
        </NavSection>

        <NavSection label="Premium">
          {premiumNavItems.map((item) => (
            <NavItem key={item.path} {...item} />
          ))}
        </NavSection>

        <NavSection label="Organize">
          {organizeNavItems.map((item) => (
            <NavItem key={item.path} {...item} />
          ))}
        </NavSection>

        {user?.role === 'admin' && (
          <NavSection label="Admin">
            <NavItem icon={Shield} label="Admin" path="/admin" />
          </NavSection>
        )}
      </nav>

      {/* Advertisement - pinned above user section */}
      <div className="flex-shrink-0 px-3">
        <SidebarAd />
      </div>

      {/* User */}
      <div className="flex-shrink-0 px-3 pt-4 pb-4 mt-2 border-t border-border-subtle">
        <div className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-background-tertiary cursor-pointer transition-colors group">
          <Avatar name={user?.name || 'User'} size="sm" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">
              {user?.name || 'Guest User'}
            </div>
            <div className="text-xs text-foreground-muted">
              {user?.plan === 'pro' ? 'Pro Plan' : 'Free Plan'}
            </div>
          </div>
          <button
            onClick={handleSignOut}
            className="p-1.5 rounded-md hover:bg-background-hover transition-all"
            title="Sign out"
          >
            <LogOut size={16} className="text-foreground-muted" />
          </button>
        </div>
      </div>
    </aside>
  );

  return (
    <>
      {sidebarContent}
      {/* Mobile backdrop */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
    </>
  );
}
